/**
 * A Company portal's own sign-in page (ADR-024 rev 5, W5c) — Fias-rendered,
 * the Company's brand by configuration, and no password field anywhere:
 *
 *   - Email → code: an emailed one-time code signs you in here.
 *   - Create an account (open portals): email, username, invite code → code.
 *   - Continue with Fias: the fias.io hand-off — the way in for accounts with
 *     an authenticator app (they get no emailed code) and anyone who prefers it.
 *
 * The disclosure is fixed text the Company cannot change; entering the code
 * with it on screen is the consent. What a reply says never depends on
 * whether an account exists: "check your email" is the answer either way.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/auth/hooks/useAuth';
import { BootSplash } from '@/components/BootSplash';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api, ApiError, type PortalSignInOptionsDto } from '@/lib/api';
import { shareOrigin } from '@/lib/portal-host';
import { queryKeys } from '@/lib/query-keys';
import {
  PortalFrame,
  PortalSignIn,
  PortalUnavailable,
  portalSignInUrl,
  safeReturnTo,
} from './PortalPages';
import { PORTAL_DISCLOSURE_VERSION, PortalDisclosure } from './PortalDisclosure';
import { portalName, usePortalProfile, type PortalProfile } from './usePortalProfile';

type Mode = 'sign_in' | 'sign_up';
type Step = 'details' | 'code';

function errorText(err: unknown): string {
  // The disclosure here is older than the one the server asks consent to: the
  // app is out of date.
  if (err instanceof ApiError && err.code === 'DISCLOSURE_REQUIRED') {
    return 'This page is out of date. Reload it, then sign in again.';
  }
  if (err instanceof ApiError) return err.message;
  return 'Something went wrong. Please try again.';
}

/** The S14 disclosure, plus what having a Fias account means. Fixed text. */
function Disclosure({ company }: { company: string }) {
  return (
    <p className="rounded-md border p-3 text-xs text-muted-foreground">
      <PortalDisclosure company={company} /> Your account is provided by Fias, and you can use it on
      fias.io too. By continuing you agree to the{' '}
      <a
        className="underline"
        href={`${shareOrigin()}/legal/terms`}
        target="_blank"
        rel="noopener noreferrer"
      >
        Fias Terms
      </a>{' '}
      and{' '}
      <a
        className="underline"
        href={`${shareOrigin()}/legal/privacy`}
        target="_blank"
        rel="noopener noreferrer"
      >
        Privacy Policy
      </a>
      .
    </p>
  );
}

function ContinueWithFias({ profile, returnTo }: { profile: PortalProfile; returnTo: string }) {
  if (!profile.signInPath) return null;
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span className="h-px flex-1 bg-border" /> or <span className="h-px flex-1 bg-border" />
      </div>
      <Button asChild variant="outline" className="w-full h-11">
        <a href={portalSignInUrl(profile.signInPath, returnTo)}>Continue with Fias</a>
      </Button>
      <p className="text-xs text-muted-foreground">
        Use this if your account has an authenticator app, or to sign in on fias.io.
      </p>
    </div>
  );
}

export function PortalSignInPage({ initialMode = 'sign_in' }: { initialMode?: Mode }) {
  const { profile, isLoading, isError, refetch } = usePortalProfile();
  const { isAuthenticated, isLoading: authLoading, retryBootProbe } = useAuth();
  const options = useQuery({
    queryKey: queryKeys.companyPortals.signInOptions(),
    queryFn: () => api.getPortalSignInOptions(),
    staleTime: 60_000,
    retry: 1,
  });
  const location = useLocation();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(
    (location.state as { from?: string } | null)?.from ?? params.get('returnTo') ?? '/',
  );

  // The server says "signed in" but this load does not know a user: either a
  // cached profile from just before the session ended, or a boot probe that
  // gave up waiting for the server's session refresh. Ask BOTH again — the
  // probe, then the profile — before concluding anything. A person the server
  // says is signed in is never shown the sign-in forms.
  const stale = Boolean(profile?.signedIn) && !authLoading && !isAuthenticated;
  const [reprobe, setReprobe] = useState<'idle' | 'running' | 'done'>('idle');
  useEffect(() => {
    if (!stale || reprobe !== 'idle') return;
    setReprobe('running');
    void retryBootProbe()
      .then(() => refetch())
      .finally(() => setReprobe('done'));
  }, [stale, reprobe, retryBootProbe, refetch]);

  if (isLoading || options.isLoading || authLoading) return <BootSplash />;
  if (isError || !profile) return <PortalUnavailable />;
  // Already signed in here — by BOTH accounts of it, never the cached profile
  // alone (which would bounce against the route guard): straight on.
  if (profile.signedIn && isAuthenticated) return <Navigate to={returnTo} replace />;
  if (stale && reprobe !== 'done') return <BootSplash />;
  if (stale) return <StillSigningIn />;
  // Emailed codes not set up here: the fias.io hand-off, as before W5c.
  if (options.isError || !options.data?.emailCode) return <PortalSignIn />;
  return (
    <SignInForms
      profile={profile}
      options={options.data}
      returnTo={returnTo}
      initialMode={options.data.signUp ? initialMode : 'sign_in'}
    />
  );
}

/** The server says signed in, this load could not confirm it twice: a reload, never the forms. */
function StillSigningIn() {
  return (
    <div className="mx-auto max-w-md p-6 text-center">
      <p className="text-sm text-muted-foreground">
        You are signed in, but this page could not finish loading your session.
      </p>
      <Button className="mt-4" onClick={() => window.location.reload()}>
        Reload
      </Button>
    </div>
  );
}

function SignInForms({
  profile,
  options,
  returnTo,
  initialMode,
}: {
  profile: PortalProfile;
  options: PortalSignInOptionsDto;
  returnTo: string;
  initialMode: Mode;
}) {
  const company = options.companyName || portalName(profile);
  const [mode, setMode] = useState<Mode>(initialMode);
  const [step, setStep] = useState<Step>('details');
  const codeInput = useRef<HTMLInputElement>(null);
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const sendCode = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      // The server remembers which code it sent; the page never needs to know
      // whether the email already had an account.
      if (mode === 'sign_up') {
        await api.startPortalSignUp({ email, username, inviteCode, returnTo });
      } else {
        await api.startPortalEmailCode(email, returnTo);
      }
      setCode('');
      setStep('code');
    });
  };

  const verify = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      // The version this page shows — a consent is to the words on screen.
      const res = await api.verifyPortalEmailCode(code.trim(), PORTAL_DISCLOSURE_VERSION);
      window.location.assign(safeReturnTo(res.redirect));
    });
  };

  // The code field takes focus when it appears, so keyboard and screen-reader
  // users land where the next thing to type is.
  useEffect(() => {
    if (step === 'code') codeInput.current?.focus();
  }, [step]);

  const title =
    step === 'code'
      ? 'Check your email'
      : mode === 'sign_up'
        ? `Join ${company}`
        : `Sign in to ${company}`;

  return (
    <PortalFrame brand={profile.brand} title={title}>
      {step === 'details' ? (
        <form className="space-y-4" onSubmit={sendCode}>
          <div className="grid gap-1.5">
            <Label htmlFor="portal-email">Email</Label>
            <Input
              id="portal-email"
              type="email"
              autoComplete="email"
              required
              maxLength={254}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          {mode === 'sign_up' && (
            <>
              <div className="grid gap-1.5">
                <Label htmlFor="portal-username">Username</Label>
                <Input
                  id="portal-username"
                  autoComplete="username"
                  required
                  maxLength={64}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="portal-invite">Invite code</Label>
                <Input
                  id="portal-invite"
                  required
                  maxLength={64}
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value)}
                />
              </div>
            </>
          )}
          <Disclosure company={company} />
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <Button type="submit" className="w-full h-11" disabled={busy || !email}>
            {busy ? 'Sending…' : mode === 'sign_up' ? 'Create account' : 'Email me a code'}
          </Button>
          {options.signUp && (
            <p className="text-center text-sm">
              {mode === 'sign_up' ? 'Already have an account? ' : 'New here? '}
              <button
                type="button"
                className="underline"
                onClick={() => {
                  setMode(mode === 'sign_up' ? 'sign_in' : 'sign_up');
                  setError(null);
                }}
              >
                {mode === 'sign_up' ? 'Sign in' : 'Create an account'}
              </button>
            </p>
          )}
          <ContinueWithFias profile={profile} returnTo={returnTo} />
        </form>
      ) : (
        <form className="space-y-4" onSubmit={verify}>
          <p className="text-sm text-muted-foreground" aria-live="polite">
            {`If ${email} can sign in here, we sent it a code. It can take a minute to arrive.`}
          </p>
          <div className="grid gap-1.5">
            <Label htmlFor="portal-code">Code</Label>
            <Input
              ref={codeInput}
              id="portal-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              maxLength={8}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            />
          </div>
          <Disclosure company={company} />
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <Button type="submit" className="w-full h-11" disabled={busy || code.length < 6}>
            {busy ? 'Checking…' : 'Continue'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="w-full"
            onClick={() => {
              setStep('details');
              setError(null);
            }}
          >
            Use a different email
          </Button>
          <ContinueWithFias profile={profile} returnTo={returnTo} />
        </form>
      )}
    </PortalFrame>
  );
}
